// Quick add → Business card (ideas track, idea 43). Take or upload a photo of a card; its text is read by:
//   1. the browser's own text recognition (TextDetector — Chromium on some systems), offline, nothing leaves the device;
//   2. else the platform's OCR provider, ONLY when the owner configured one (off by default; the photo is not stored);
//   3. else the advisor pastes or types the text (or uses the phone's own "copy text from photo").
// The text is split into fields by rules (domain/business-card.ts); the advisor reviews every field, then the person is
// created with the source «Γνωριμία / met in person». Nothing is saved before «Save».
import { useState } from 'react';
import { parseBusinessCard, createPersonFromCard, cardNote, type CardFields } from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Field, Modal } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';
import { accountService } from '../core/account-service.ts';

type Ev = { target: HTMLInputElement };
type TaEv = { target: HTMLTextAreaElement };
type Detector = { detect(src: ImageBitmapSource): Promise<{ rawValue: string; boundingBox?: DOMRectReadOnly }[]> };

/** the browser's built-in text recognition, if it has one */
async function browserText(file: Blob): Promise<string | null> {
  const TD = (globalThis as { TextDetector?: new () => Detector }).TextDetector;
  if (!TD || typeof createImageBitmap !== 'function') return null;
  try {
    const found = await new TD().detect(await createImageBitmap(file));
    if (!found.length) return null;
    // top-to-bottom, left-to-right: one line per detected block
    return found.sort((a, b) => (a.boundingBox?.y ?? 0) - (b.boundingBox?.y ?? 0) || (a.boundingBox?.x ?? 0) - (b.boundingBox?.x ?? 0)).map((x) => x.rawValue).join('\n');
  } catch { return null; }
}

const FIELDS = ['firstName', 'lastName', 'mobile', 'phone', 'email', 'company', 'jobTitle', 'website', 'address'] as const;

export function CardScan() {
  const rt = useRuntime();
  const { t } = rt;
  const [text, setText] = useState('');
  const [f, setF] = useState<CardFields | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const read = (x: string) => { setText(x); setF(parseBusinessCard(x)); };
  const photo = async (file: File | null) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setMsg(t('card.too_large')); return; }
    setPreview(URL.createObjectURL(file)); setBusy(true); setMsg(null);
    try {
      const local = await browserText(file);
      if (local) { read(local); setMsg(t('card.read_device')); return; }
      const a = accountService();
      if (a?.snapshot().signedIn) {
        try { const r = await a.api.ocrCard(file); if (r.text.trim()) { read(r.text); setMsg(t('card.read_platform')); return; } }
        catch (e) { if ((e as Error).message !== 'ocr_not_configured') { setMsg(t('card.read_failed')); return; } }
      }
      setMsg(t('card.no_ocr'));
    } finally { setBusy(false); }
  };
  const set = (k: keyof CardFields) => (e: Ev) => f && setF({ ...f, [k]: e.target.value, ...(k === 'firstName' || k === 'lastName' ? { name: '' } : {}) });
  const save = async () => {
    if (!f) return;
    const name = [f.firstName, f.lastName].map((x) => x.trim()).filter(Boolean).join(' ');
    const note = cardNote(f, { website: t('card.field.website'), address: t('card.field.address'), phone: t('card.field.phone'), from: t('card.note_from', { date: rt.i18n.date(rt.today) }) });
    const cs = await rt.run((v, c) => createPersonFromCard(v, { ...f, name, note }, c), 'person.created', { name });
    if (cs) { rt.close(); navigate(personPath(cs.entityId!)); }
  };
  const dup = f && (f.email || f.mobile || f.phone) ? rt.view.rows('persons').find((p) => !p.archivedAt && ((f.email && p.primaryEmail?.toLowerCase() === f.email.toLowerCase()) || ([f.mobile, f.phone].some((x) => x && p.primaryPhone && x.replace(/\D/g, '').endsWith(p.primaryPhone.replace(/\D/g, '').slice(-8)))))) : undefined;
  return (
    <Modal wide title={t('card.title')} footer={<><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" disabled={!f || !(f.firstName || f.lastName)} onClick={() => void save()} data-testid="card-save">{t('common.save')}</button></>}>
      <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 18 }} data-testid="card-scan">
        <div className="stack">
          <label className="btn btn-flow" style={{ justifyContent: 'center' }}><Icon name="upload" />{busy ? t('card.reading') : t('card.photo')}
            <input type="file" accept="image/*" capture="environment" hidden onChange={(e: Ev) => void photo(e.target.files?.[0] ?? null)} data-testid="card-photo" /></label>
          {preview && <img src={preview} alt={t('card.photo_alt')} style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }} />}
          {msg && <p className="small muted" style={{ margin: 0 }} role="status">{msg}</p>}
          <Field label={t('card.text')} hint={t('card.text_hint')}><textarea className="textarea" rows={8} value={text} onChange={(e: TaEv) => setText(e.target.value)} data-testid="card-text" /></Field>
          <button className="btn" disabled={!text.trim()} onClick={() => read(text)} data-testid="card-read"><Icon name="spark" />{t('card.read')}</button>
        </div>
        <div className="stack">
          {f ? <>
            <div className="upper">{t('card.review')}</div>
            <div className="form-grid">{FIELDS.map((k) => <Field key={k} label={t(`card.field.${k}`)}><input className="input" value={f[k]} onChange={set(k)} data-testid={`card-${k}`} /></Field>)}</div>
            {f.rest.length > 0 && <p className="tiny muted" style={{ margin: 0 }}>{t('card.rest', { lines: f.rest.join(' · ') })}</p>}
            {dup && <div className="banner">{t('person.duplicate_warning', { name: dup.displayName })}<button className="btn btn-sm" onClick={() => { rt.close(); navigate(personPath(dup.id)); }}>{t('common.open')}</button></div>}
            <p className="tiny muted" style={{ margin: 0 }}>{t('card.source_note')}</p>
          </> : <p className="small muted">{t('card.empty')}</p>}
        </div>
      </div>
      <p className="tiny muted" style={{ marginBottom: 0 }}>{t('card.privacy')}</p>
    </Modal>
  );
}
